import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AvatarPicker from '../components/AvatarPicker.jsx';
import StudentPlay from './StudentPlay.jsx';
import { http } from '../api/http.js';
import { getSocket, emitAck } from '../api/socket.js';
import { loadSavedPlayer, savePlayer, clearSavedPlayer } from '../hooks/useLocalToken.js';
import { getLessonOrGeneric } from '../constants/lessons.js';
import { ArrowRightIcon } from '../components/Icons.jsx';
import '../styles/join.css';
import '../styles/themeTag.css';

const NICKNAME_MAX_LEN = 24;

export default function StudentApp() {
  const { roomCode: roomCodeParam } = useParams();
  const navigate = useNavigate();

  const [codeInput, setCodeInput] = useState(roomCodeParam || '');
  const [roomCode, setRoomCode] = useState(roomCodeParam ? roomCodeParam.toUpperCase() : null);
  // A lesson with its own look (PYVAR2's theme A) carries it from the door.
  const joinTheme = roomCode ? getLessonOrGeneric(roomCode).theme : undefined;
  const [roomStatus, setRoomStatus] = useState(null); // null | 'checking' | 'ok' | 'not-found'

  const [nickname, setNickname] = useState('');
  const [nicknameError, setNicknameError] = useState('');
  const [avatar, setAvatar] = useState('code');

  const [saved, setSaved] = useState(null);
  const [claimPrompt, setClaimPrompt] = useState(null); // { existingPlayerId }
  const [joinError, setJoinError] = useState('');
  const [busy, setBusy] = useState(false);
  const [introSeen, setIntroSeen] = useState(false);

  const [session, setSession] = useState(null); // { token, player } once joined

  // Resolve room code -> validate it exists before showing the join form.
  useEffect(() => {
    if (!roomCode) return;
    setRoomStatus('checking');
    http
      .get(`/api/public/rooms/${roomCode}`)
      .then(() => setRoomStatus('ok'))
      .catch(() => setRoomStatus('not-found'));
    setSaved(loadSavedPlayer(roomCode));
  }, [roomCode]);

  const submitCode = (e) => {
    e.preventDefault();
    const clean = codeInput.trim().toUpperCase();
    if (!clean) return;
    navigate(`/join/${clean}`);
    setRoomCode(clean);
  };

  const doJoin = useCallback(
    async (extra = {}) => {
      setJoinError('');
      setBusy(true);
      try {
        const socket = getSocket();
        const result = await emitAck('player:join', {
          roomCode,
          nickname,
          avatar,
          ...extra,
        });
        if (result.ok) {
          savePlayer(roomCode, result.token, { nickname: result.player.nickname, avatar: result.player.avatar });
          setSession({ token: result.token, player: result.player });
          setClaimPrompt(null);
        } else if (result.reason === 'confirm-claim') {
          setClaimPrompt({ existingPlayerId: result.existingPlayerId });
        } else if (result.reason === 'nickname-taken') {
          setJoinError('ชื่อนี้มีผู้ใช้งานอยู่ในห้องแล้ว โปรดใช้ชื่ออื่น');
        } else if (result.reason === 'invalid-nickname') {
          setNicknameError('กรุณากรอกชื่อเล่น');
        } else {
          setJoinError('เข้าร่วมห้องไม่สำเร็จ ลองใหม่อีกครั้ง');
        }
      } catch {
        setJoinError('เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ ลองใหม่อีกครั้ง');
      } finally {
        setBusy(false);
      }
    },
    [roomCode, nickname, avatar]
  );

  const rejoinAsSaved = async () => {
    setBusy(true);
    setJoinError('');
    try {
      const result = await emitAck('player:join', { roomCode, token: saved.token });
      if (result.ok) {
        savePlayer(roomCode, result.token, { nickname: result.player.nickname, avatar: result.player.avatar });
        setSession({ token: result.token, player: result.player });
      } else {
        clearSavedPlayer(roomCode);
        setSaved(null);
        setJoinError('กลับเข้าห้องด้วยข้อมูลเดิมไม่สำเร็จ กรุณาเข้าร่วมใหม่');
      }
    } catch {
      setJoinError('เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ ลองใหม่อีกครั้ง');
    } finally {
      setBusy(false);
    }
  };

  const validateAndJoin = (e) => {
    e.preventDefault();
    const trimmed = nickname.trim();
    if (!trimmed) {
      setNicknameError('กรุณากรอกชื่อเล่นก่อนเข้าร่วม');
      return;
    }
    setNicknameError('');
    doJoin();
  };

  if (session) {
    return <StudentPlay roomCode={roomCode} token={session.token} initialPlayer={session.player} />;
  }

  // --- Step 0: no room code yet (landed on "/") ---
  if (!roomCode) {
    return (
      <div className="page join-page" data-theme={joinTheme}>
        <div className="page-narrow join-card-wrap">
          <div className="card join-card-accent join-card stack">
            <div className="text-center">
              <h1>เข้าสู่บทเรียน</h1>
              <p className="text-dim">กรอกรหัสเข้าเรียนที่แสดงบนหน้าจอ</p>
            </div>
            <form onSubmit={submitCode} className="stack">
              <div className="field join-pin-field">
                <label htmlFor="room-code">รหัสเข้าเรียน</label>
                <input
                  id="room-code"
                  className="join-pin-input"
                  value={codeInput}
                  onChange={(e) => setCodeInput(e.target.value)}
                  placeholder="เช่น PYINIT"
                  maxLength={12}
                  autoFocus
                  autoComplete="off"
                  autoCapitalize="characters"
                  spellCheck="false"
                />
              </div>
              <button className="btn btn-primary btn-block join-cta" type="submit">
                <span>ถัดไป</span>
                <ArrowRightIcon size={16} />
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (roomStatus === 'checking' || roomStatus === null) {
    return (
      <div className="page join-page" data-theme={joinTheme}>
        <p className="text-dim">กำลังตรวจสอบสถานะห้อง...</p>
      </div>
    );
  }

  if (roomStatus === 'not-found') {
    return (
      <div className="page join-page" data-theme={joinTheme}>
        <div className="page-narrow card join-card-accent stack text-center">
          <h2>ไม่พบรหัสห้อง "{roomCode}"</h2>
          <p className="text-dim">โปรดตรวจสอบรหัสห้องอีกครั้งหรือสอบถามผู้ดูแลห้อง</p>
          <button className="btn btn-ghost" onClick={() => { setRoomCode(null); navigate('/'); }}>
            ลองกรอกรหัสใหม่
          </button>
        </div>
      </div>
    );
  }

  // --- Step 2: Reconnection prompt if previous token exists ---
  if (saved && !claimPrompt) {
    const savedName = saved.nickname || saved.profile?.nickname || 'ผู้เรียนเดิม';
    return (
      <div className="page join-page" data-theme={joinTheme}>
        <div className="page-narrow card join-card-accent stack">
          <div>
            <h1>ยินดีต้อนรับกลับ</h1>
            <p className="text-dim">ห้อง {roomCode}</p>
          </div>
          <p>
            คุณเคยเข้าร่วมด้วยชื่อ <strong>"{savedName}"</strong> ต้องการกลับเข้าห้องเดิมใช่หรือไม่?
          </p>
          {joinError && <p className="field-error">{joinError}</p>}
          <button className="btn btn-primary btn-block" disabled={busy} onClick={rejoinAsSaved}>
            {busy ? 'กำลังเข้าสู่ห้อง...' : `กลับเข้าห้องในชื่อ "${savedName}"`}
          </button>
          <button
            className="btn btn-ghost btn-block"
            disabled={busy}
            onClick={() => {
              clearSavedPlayer(roomCode);
              setSaved(null);
            }}
          >
            เข้าร่วมด้วยชื่อใหม่
          </button>
        </div>
      </div>
    );
  }

  // --- Step 3: Name collision with disconnected player ---
  if (claimPrompt) {
    return (
      <div className="page join-page" data-theme={joinTheme}>
        <div className="page-narrow card join-card-accent stack">
          <h2>ยืนยันตัวตน</h2>
          <p>
            มีชื่อ <strong>{nickname}</strong> เคยอยู่ในห้องนี้แล้ว คุณคือบุคคลเดิมที่หลุดไปใช่หรือไม่?
          </p>
          {joinError && <p className="field-error">{joinError}</p>}
          <button
            className="btn btn-primary btn-block"
            disabled={busy}
            onClick={() => doJoin({ confirmClaimPlayerId: claimPrompt.existingPlayerId })}
          >
            {busy ? 'กำลังยืนยัน...' : 'ใช่ กลับเข้าห้องด้วยชื่อนี้'}
          </button>
          <button className="btn btn-ghost btn-block" disabled={busy} onClick={() => setClaimPrompt(null)}>
            ไม่ใช่ ฉันจะเปลี่ยนชื่อ
          </button>
        </div>
      </div>
    );
  }

  // --- Step 3.5: Lesson intro (new joiners only; returning students skip it) ---
  if (!introSeen) {
    const lesson = getLessonOrGeneric(roomCode);
    return (
      <div className="page join-page" data-theme={joinTheme}>
        <div className="page-narrow join-card-wrap">
          <div className="card join-card-accent stack lesson-intro">
            <div className="lesson-intro-head">
              <span className="lesson-intro-unit">{lesson.unit}</span>
              <h1 className="lesson-intro-title">{lesson.title}</h1>
              {lesson.subtitle && <p className="text-dim lesson-intro-sub">{lesson.subtitle}</p>}
            </div>

            <div className="lesson-intro-meta row">
              {lesson.instructor && (
                <span className="lesson-intro-teacher">ผู้สอน · {lesson.instructor}</span>
              )}
              {lesson.questionCount != null && (
                <span className="lesson-intro-count num">{lesson.questionCount} ข้อ</span>
              )}
            </div>

            {lesson.objectives.length > 0 && (
              <div className="lesson-intro-objectives">
                <span className="lesson-intro-objectives-label">สิ่งที่จะได้เรียนรู้</span>
                <ol>
                  {lesson.objectives.map((o) => (
                    <li key={o.id}>{o.text}</li>
                  ))}
                </ol>
              </div>
            )}

            <button className="btn btn-primary btn-block join-cta" type="button" onClick={() => setIntroSeen(true)}>
              <span>เริ่มเข้าเรียน</span>
              <ArrowRightIcon size={16} />
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-block btn-sm"
              onClick={() => { setRoomCode(null); navigate('/'); }}
            >
              เปลี่ยนรหัสเข้าเรียน
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- Step 4: Fresh join form ---
  return (
    <div className="page join-page" data-theme={joinTheme}>
      <form className="page-narrow card join-card-accent stack" onSubmit={validateAndJoin}>
        <div className="row-between">
          <div>
            <h1>ตั้งชื่อเข้าห้อง</h1>
            <p className="text-dim">รหัสเข้าเรียน: <strong className="text-accent">{roomCode}</strong></p>
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setIntroSeen(false)}>
            ย้อนกลับ
          </button>
        </div>

        <div className="field">
          <label htmlFor="nickname">ชื่อเล่น</label>
          <input
            id="nickname"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder="กรอกชื่อของคุณ"
            maxLength={NICKNAME_MAX_LEN}
            autoFocus
          />
          {nicknameError && <p className="field-error">{nicknameError}</p>}
        </div>

        <AvatarPicker value={avatar} onChange={setAvatar} />

        {joinError && <p className="field-error">{joinError}</p>}

        <button className="btn btn-primary btn-block" type="submit" disabled={busy}>
          <span>{busy ? 'กำลังเข้าร่วม...' : 'เข้าสู่บทเรียน'}</span>
          {!busy && <ArrowRightIcon size={16} />}
        </button>
      </form>
    </div>
  );
}
